/**
 * Mock processing history data for StarNexus platform
 * @module data/starnexus/mockProcessingHistory
 */

import type { ProcessingFile, ProcessingEvent, FileStatus } from '../../types/starnexus';

// Helper to create dates relative to now
const daysAgo = (days: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

const hoursAgo = (hours: number): Date => {
  const date = new Date();
  date.setHours(date.getHours() - hours);
  return date;
};

const minutesAgo = (minutes: number): Date => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - minutes);
  return date;
};

// Mock processing files
export const mockProcessingFiles: ProcessingFile[] = [
  {
    fileId: 'file-001',
    fileName: 'sales_2025_01_15.csv',
    datasetId: 'ds-001',
    status: 'completed',
    fileSize: 2500000,
    recordCount: 15000,
    uploadedAt: hoursAgo(2),
    processedAt: hoursAgo(1.5),
    processingDuration: 45
  },
  {
    fileId: 'file-002',
    fileName: 'sales_2025_01_14.csv',
    datasetId: 'ds-001',
    status: 'completed',
    fileSize: 2300000,
    recordCount: 14200,
    uploadedAt: daysAgo(1),
    processedAt: daysAgo(1),
    processingDuration: 42
  },
  {
    fileId: 'file-003',
    fileName: 'inventory_batch_001.json',
    datasetId: 'ds-002',
    status: 'processing',
    fileSize: 1500000,
    recordCount: 8500,
    uploadedAt: minutesAgo(10),
    progress: 65
  },
  {
    fileId: 'file-004',
    fileName: 'inventory_batch_002.json',
    datasetId: 'ds-002',
    status: 'queued',
    fileSize: 1800000,
    uploadedAt: minutesAgo(5)
  },
  {
    fileId: 'file-005',
    fileName: 'feedback_survey_jan.xlsx',
    datasetId: 'ds-003',
    status: 'failed',
    fileSize: 500000,
    uploadedAt: daysAgo(1),
    errorMessage: 'Schema validation failed: missing required column "response_id"'
  },
  {
    fileId: 'file-006',
    fileName: 'orders_2025_01_15.csv',
    datasetId: 'ds-004',
    status: 'processing',
    fileSize: 3200000,
    recordCount: 25000,
    uploadedAt: minutesAgo(15),
    progress: 82
  },
  {
    fileId: 'file-007',
    fileName: 'product_updates.json',
    datasetId: 'ds-006',
    status: 'failed',
    fileSize: 800000,
    uploadedAt: hoursAgo(6),
    errorMessage: 'Connection timeout while writing to Andes'
  },
  {
    fileId: 'file-008',
    fileName: 'invoices_jan_2025.xlsx',
    datasetId: 'ds-007',
    status: 'completed',
    fileSize: 1200000,
    recordCount: 560,
    uploadedAt: hoursAgo(4),
    processedAt: hoursAgo(3.5),
    processingDuration: 90
  },
  {
    fileId: 'file-009',
    fileName: 'sessions_2025_01_15_00.json',
    datasetId: 'ds-008',
    status: 'completed',
    fileSize: 5000000,
    recordCount: 45000,
    uploadedAt: hoursAgo(1),
    processedAt: minutesAgo(50),
    processingDuration: 8
  },
  {
    fileId: 'file-010',
    fileName: 'sessions_2025_01_15_01.json',
    datasetId: 'ds-008',
    status: 'processing',
    fileSize: 4800000,
    recordCount: 42000,
    uploadedAt: minutesAgo(5),
    progress: 45
  }
];

// Mock processing events
export const mockProcessingEvents: ProcessingEvent[] = [
  {
    eventId: 'evt-001',
    datasetId: 'ds-001',
    datasetName: 'sales-transactions-2025',
    eventType: 'file_processed',
    status: 'success',
    timestamp: hoursAgo(1.5),
    details: {
      fileName: 'sales_2025_01_15.csv',
      recordCount: 15000,
      duration: 45
    },
    userId: 'user-001',
    userName: 'Alice Chen'
  },
  {
    eventId: 'evt-002',
    datasetId: 'ds-002',
    datasetName: 'inventory-updates',
    eventType: 'file_uploaded',
    status: 'info',
    timestamp: minutesAgo(10),
    details: {
      fileName: 'inventory_batch_001.json',
      fileSize: 1500000
    },
    userId: 'user-002',
    userName: 'Bob Smith'
  },
  // Andes access events
  {
    eventId: 'evt-andes-001',
    datasetId: 'ds-001',
    datasetName: 'bronze_rpt_cds_retail_cga',
    eventType: 'permission_approved',
    status: 'success',
    timestamp: minutesAgo(25),
    details: {
      identityType: 'CRADLE_ACCOUNT',
      identityValue: 'aurora-weaver-cradle-prod',
      approvedBy: 'admin'
    },
    userId: 'user-001',
    userName: 'Alice Chen'
  },
  {
    eventId: 'evt-andes-002',
    datasetId: 'ds-002',
    datasetName: 'gold_shipment_metrics',
    eventType: 'subscription_activated',
    status: 'success',
    timestamp: minutesAgo(45),
    details: {
      targetName: 'delta_site_prod_amazon_bi',
      targetType: 'DATASHARE',
      region: 'us-east-1'
    },
    userId: 'user-002',
    userName: 'Bob Smith'
  },
  {
    eventId: 'evt-003',
    datasetId: 'ds-003',
    datasetName: 'customer-feedback',
    eventType: 'file_failed',
    status: 'error',
    timestamp: daysAgo(1),
    details: {
      fileName: 'feedback_survey_jan.xlsx',
      error: 'Schema validation failed'
    },
    userId: 'user-003',
    userName: 'Carol Davis'
  },
  {
    eventId: 'evt-004',
    datasetId: 'ds-004',
    datasetName: 'order-fulfillment',
    eventType: 'processing_started',
    status: 'info',
    timestamp: minutesAgo(15),
    details: {
      fileName: 'orders_2025_01_15.csv',
      estimatedDuration: 35
    },
    userId: 'user-004',
    userName: 'David Lee'
  },
  {
    eventId: 'evt-andes-003',
    datasetId: 'ds-003',
    datasetName: 'bronze_raw_events',
    eventType: 'permission_requested',
    status: 'info',
    timestamp: hoursAgo(2),
    details: {
      identityType: 'IAM_ROLE',
      identityValue: 'arn:aws:iam::123456789012:role/DataAnalystRole'
    },
    userId: 'user-003',
    userName: 'Carol Davis'
  },
  {
    eventId: 'evt-005',
    datasetId: 'ds-006',
    datasetName: 'product-catalog',
    eventType: 'file_failed',
    status: 'error',
    timestamp: hoursAgo(6),
    details: {
      fileName: 'product_updates.json',
      error: 'Connection timeout'
    },
    userId: 'user-001',
    userName: 'Alice Chen'
  },
  {
    eventId: 'evt-006',
    datasetId: 'ds-001',
    datasetName: 'sales-transactions-2025',
    eventType: 'config_updated',
    status: 'info',
    timestamp: hoursAgo(3),
    details: {
      field: 'concurrencyLimit',
      oldValue: 8,
      newValue: 10
    },
    userId: 'user-001',
    userName: 'Alice Chen'
  },
  {
    eventId: 'evt-andes-004',
    datasetId: 'ds-004',
    datasetName: 'silver_order_summary',
    eventType: 'subscription_created',
    status: 'info',
    timestamp: hoursAgo(4),
    details: {
      targetName: 'aurora-weaver-cradle-prod',
      targetType: 'CRADLE',
      region: 'us-west-2'
    },
    userId: 'user-004',
    userName: 'David Lee'
  },
  {
    eventId: 'evt-007',
    datasetId: 'ds-008',
    datasetName: 'website-analytics',
    eventType: 'file_processed',
    status: 'success',
    timestamp: minutesAgo(50),
    details: {
      fileName: 'sessions_2025_01_15_00.json',
      recordCount: 45000,
      duration: 8
    },
    userId: 'user-005',
    userName: 'Emma Wilson'
  },
  {
    eventId: 'evt-008',
    datasetId: 'ds-007',
    datasetName: 'supplier-invoices',
    eventType: 'file_processed',
    status: 'success',
    timestamp: hoursAgo(3.5),
    details: {
      fileName: 'invoices_jan_2025.xlsx',
      recordCount: 560,
      duration: 90
    },
    userId: 'user-006',
    userName: 'Frank Miller'
  },
  {
    eventId: 'evt-andes-005',
    datasetId: 'ds-005',
    datasetName: 'gold_customer_360',
    eventType: 'permission_rejected',
    status: 'warning',
    timestamp: hoursAgo(5),
    details: {
      identityType: 'TEAM',
      identityValue: 'amzn1.abacus.team.external123',
      rejectedBy: 'admin',
      reason: 'External team access not permitted'
    },
    userId: 'user-005',
    userName: 'Emma Wilson'
  },
  {
    eventId: 'evt-009',
    datasetId: 'ds-010',
    datasetName: 'support-tickets',
    eventType: 'dataset_enabled',
    status: 'success',
    timestamp: hoursAgo(5),
    details: {
      previousStatus: 'inactive'
    },
    userId: 'user-003',
    userName: 'Carol Davis'
  },
  {
    eventId: 'evt-andes-006',
    datasetId: 'ds-006',
    datasetName: 'bronze_product_feed',
    eventType: 'subscription_failed',
    status: 'error',
    timestamp: hoursAgo(8),
    details: {
      targetName: 'aurora_weaver_glue_dev',
      targetType: 'GLUE',
      error: 'Target not available in region'
    },
    userId: 'user-006',
    userName: 'Frank Miller'
  },
  {
    eventId: 'evt-010',
    datasetId: 'ds-005',
    datasetName: 'marketing-campaigns',
    eventType: 'dataset_disabled',
    status: 'warning',
    timestamp: daysAgo(14),
    details: {
      reason: 'Campaign ended'
    },
    userId: 'user-005',
    userName: 'Emma Wilson'
  }
];

// Export helper functions
export const getFilesByDataset = (datasetId: string): ProcessingFile[] => {
  return mockProcessingFiles.filter((f) => f.datasetId === datasetId);
};

export const getFilesByStatus = (status: FileStatus): ProcessingFile[] => {
  return mockProcessingFiles.filter((f) => f.status === status);
};

export const getEventsByDataset = (datasetId: string): ProcessingEvent[] => {
  return mockProcessingEvents.filter((e) => e.datasetId === datasetId);
};

export const getRecentEvents = (limit: number = 10): ProcessingEvent[] => {
  return [...mockProcessingEvents].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).slice(0, limit);
};

export const getActiveProcessingFiles = (): ProcessingFile[] => {
  return mockProcessingFiles.filter((f) => f.status === 'processing' || f.status === 'queued');
};
